import type {
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import type {
  CandidateBasicDto,
  CandidateProfileImageRequest,
  CvAnalysisBasicDto,
  CvResponse,
  CvUpdateRequest,
  UserDto,
} from '@/services/api/codegen/api';
import {
  CandidateProfileService,
  CvAnalysisService,
  TrainingService,
} from '@/services/api/codegen/api';

const accountSettingsKeys = {
  getCvAnalysis: (params: GetCvAnalysisQueryParams) =>
    ['getCvAnalysis', params] as const,
  getTrainingInterviews: (params: GetTrainingInterviewsQueryParams) =>
    ['getTrainingInterviews', params] as const,
  getCandidateProfileCv: () => ['getCandidateProfileCv'] as const,
  getCandidateProfileMe: () => ['getCandidateProfileMe'] as const,
};

type GetCvAnalysisQueryParams = {
  q?: string;
  aiScoreFrom?: number;
  aiScoreTo?: number;
  dateFrom?: Date;
  dateTo?: Date;
};

export const useGetCvAnalysis = (
  params: GetCvAnalysisQueryParams,
  options?: Partial<UseQueryOptions<Array<CvAnalysisBasicDto>, AxiosError>>
) => {
  return useQuery<Array<CvAnalysisBasicDto>, AxiosError>({
    queryKey: accountSettingsKeys.getCvAnalysis(params),
    queryFn: () =>
      CvAnalysisService.getCvAnalysis({
        ...params,
        dateFrom: params.dateFrom?.toISOString(),
        dateTo: params.dateTo?.toISOString(),
      }),
    ...options,
  });
};

type GetTrainingInterviewsQueryParams = {
  q?: string;
  aiScoreFrom?: number;
  aiScoreTo?: number;
  dateFrom?: Date;
  dateTo?: Date;
};

export const useGetTrainingInterviews = (
  params: GetTrainingInterviewsQueryParams,
  options?: Partial<UseQueryOptions<Array<CandidateBasicDto>, AxiosError>>
) => {
  return useQuery<Array<CandidateBasicDto>, AxiosError>({
    queryKey: accountSettingsKeys.getTrainingInterviews(params),
    queryFn: () =>
      TrainingService.getTrainingInterview({
        ...params,
        dateFrom: params.dateFrom?.toISOString(),
        dateTo: params.dateTo?.toISOString(),
      }),
    ...options,
  });
};

export const useGetCandidateProfileInfo = (
  options?: Partial<UseQueryOptions<CvResponse, AxiosError>>
) => {
  return useQuery<CvResponse, AxiosError>({
    queryKey: accountSettingsKeys.getCandidateProfileCv(),
    queryFn: () => CandidateProfileService.getCandidateProfileCv(),
    ...options,
  });
};

export const useUpdateCandidateProfileInfo = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, { body: CvUpdateRequest }>
  >
) => {
  return useMutation<void, AxiosError, { body: CvUpdateRequest }>({
    mutationFn: (params) =>
      CandidateProfileService.putCandidateProfileCv(params),
    ...options,
  });
};

export const useUpdateCandidateProfilePicture = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, { body: CandidateProfileImageRequest }>
  >
) => {
  return useMutation<void, AxiosError, { body: CandidateProfileImageRequest }>({
    mutationFn: (params) =>
      CandidateProfileService.putCandidateProfileProfileImage(params),
    ...options,
  });
};

export const useGetCandidateProfileMe = (
  options?: Partial<UseQueryOptions<UserDto, AxiosError>>
) => {
  return useQuery<UserDto, AxiosError>({
    queryKey: accountSettingsKeys.getCandidateProfileMe(),
    queryFn: () =>
      CandidateProfileService.getCandidateProfileMe(
        options?.axiosRequestConfig
      ),
    ...options,
  });
};
